/**
 * Free gift tiers (snippets/gift-tiers.liquid).
 * Keeps one gift line per reached tier (line property _gift: 1|2|3) and swaps the gift color.
 * Only uses the public MaxDesign API (controller.editCart), no MaxDesign file is modified.
 * MaxDesign re-renders the cart markup on each change, so every render connects a new <gift-tiers>.
 */

// Cart requests run one after another, so concurrent renders can't add the same gift twice
let queue = Promise.resolve();
// Last fix sent; if a render asks for the same fix again, the request failed and we don't retry it
let lastFix = '';

class GiftTiers extends HTMLElement {
  async connectedCallback() {
    await customElements.whenDefined('cart-controller-maxdesign');
    this.controller = MaxDesign.cart.controller;
    this.state = JSON.parse(this.querySelector('[data-gift-tiers-state]').textContent);
    this.addEventListener('change', this.onSelectChange.bind(this));
    this.enqueue(() => this.sync());
  }

  enqueue(task) {
    queue = queue.then(() => this.isConnected && task()).catch(console.error);
  }

  /**
   * Remove gifts above the reached tier or duplicated, reset quantities to 1, add missing gifts
   */
  async sync() {
    const { reached, variants, lines } = this.state;
    const updates = {};
    const filled = new Set();
    for (const { key, slot, quantity } of lines) {
      if (slot < 1 || slot > reached || filled.has(slot)) updates[key] = 0;
      else {
        filled.add(slot);
        if (quantity !== 1) updates[key] = 1;
      }
    }
    const items = [];
    for (let slot = 1; slot <= reached; slot++) {
      // Each tier has its own gift product
      const variant = variants[slot - 1];
      if (variant && !filled.has(slot)) items.push({ id: variant, quantity: 1, properties: { _gift: String(slot) } });
    }

    const hasUpdates = Object.keys(updates).length > 0;
    if (!hasUpdates && !items.length) return (lastFix = '');
    const fix = JSON.stringify({ updates, items });
    if (fix === lastFix) return;
    lastFix = fix;

    const options = { scroll: false, openModal: false };
    if (hasUpdates) await this.controller.editCart('update', { data: { updates }, ...options, render: !items.length });
    if (items.length) await this.controller.editCart('add', { data: { items }, ...options });
  }

  /**
   * Swap a gift color: add the new variant, then remove the old line only if the add went through
   * (editCart catches request errors itself, so we check the cart)
   */
  onSelectChange(e) {
    const select = e.target.closest('[data-gift-slot]');
    if (!select) return;
    const slot = Number(select.dataset.giftSlot);
    const line = this.state.lines.find(line => line.slot === slot);
    const item = { id: Number(select.value), quantity: 1, properties: { _gift: String(slot) } };
    const options = { scroll: false, openModal: false };

    this.enqueue(async () => {
      await this.controller.editCart('add', { data: { items: [item] }, ...options, render: !line });
      if (!line) return;
      const cart = await fetch(`${Shopify.routes.root}cart.js`).then(response => response.json());
      const added = cart.items.some(({ variant_id, properties }) => variant_id === item.id && properties?._gift === item.properties._gift);
      if (added) await this.controller.editCart('update', { data: { updates: { [line.key]: 0 } }, ...options });
      else this.controller.render(await this.controller.fetch()); // reset the select to the current gift
    });
  }
}

customElements.get('gift-tiers') || customElements.define('gift-tiers', GiftTiers);
